import { API } from "apis";
import Chip from "components/common/Chip";
import PageTitle from "components/common/PageTitle";
import RowsPerPageSelect from "components/common/RowsPerPageSelect";
import PageLabel from "components/common/paging/PageLabel";
import Pagination from "components/common/paging/Pagination2";
import ThreadSearchOptionsArea from "components/thread/list/ThreadSearchOptionsArea";
import { PER_PAGE_TYPES } from "enums";
import { ContextModel, ThreadListModel } from "models";
import { ThreadSearchOptions } from "models/ThreadSearchOptions";
import qs from "query-string";
import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { Utils } from "utils";

const TalkListPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();

  const location = useLocation();
  const navigate = useNavigate();

  // 直前に検索を行ったクエリ
  const searchedQuery = React.useRef<string>();
  // 検索オプション
  const options = React.useRef<ThreadSearchOptions>({});
  const [data, setData] = React.useState<ThreadListModel>();

  const reload = async () => {
    for (let key in options.current) {
      if ((options.current as any)[key] === "") {
        delete (options.current as any)[key];
      }
    }
    const params = qs.stringify(options.current);
    navigate({ pathname: location.pathname, search: params });
  };

  const onSearch = async () => {
    delete options.current.page_no;
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;

    reload();
  };

  const onChangePerPage = async () => {
    delete options.current.page_no;

    reload();
  };

  const onChangePage = async (value: number) => {
    if (value <= 1) {
      delete options.current.page_no;
    } else {
      options.current.page_no = value;
    }

    reload();
  };

  React.useEffect(() => {
    if (searchedQuery.current === location.search) {
      console.log("useEffect => load cancel.");
      return;
    }
    console.log("useEffect => load start. : " + location.search);
    searchedQuery.current = location.search;
    const f = async () => {
      ctx.loading.setState(true);
      if (!ctx.auth.data) {
        await ctx.auth.initialize();
      }
      if (!ctx.limitPrice.data) {
        await ctx.limitPrice.load();
      }

      // optionsを一度クリアしてから、クエリの状態に
      for (let k in options.current) {
        delete (options.current as any)[k];
      }
      const query = qs.parse(location.search);
      if (query.page_no !== undefined) {
        options.current.page_no = Number(query.page_no);
      }
      if (query.thread_send_receive_status !== undefined) {
        options.current.thread_send_receive_status = Number(
          query.thread_send_receive_status
        );
      }
      if (query.keyword !== undefined) {
        options.current.keyword = String(query.keyword);
      }
      if (query.is_show_all !== undefined) {
        options.current.is_show_all = query.is_show_all === "true";
      }
      if (query.get_count !== undefined) {
        options.current.get_count = Number(query.get_count);
      }

      try {
        const response = await API.Thread.search(options.current);
        console.log(response);
        setData(response.data);
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });

      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!data) {
    return <div></div>;
  }

  return (
    <div>
      <Helmet>
        <title>見積依頼・お問い合わせ一覧</title>
      </Helmet>
      <PageTitle title="見積依頼・お問い合わせ一覧" className="p-2" />

      <ThreadSearchOptionsArea
        options={options.current}
        data={data}
        onSearch={onSearch}
      />

      {data.threads.length === 0 ? (
        <div className="flex justify-center py-12">
          <div>検索結果がありません。</div>
        </div>
      ) : (
        <div className="m-4">
          <div className="mb-2">
            <RowsPerPageSelect options={options.current} onChange={onChangePerPage} />
            <span>を表示</span>
          </div>
          <PageLabel data={data} />
          <table className="w-full table-fixed">
            <thead className="border-b">
              <tr>
                <th className="w-28">状態</th>
                <th className="text-left">会社名</th>
                <th className="text-left">件名</th>
                <th className="w-36">最終更新</th>
              </tr>
            </thead>
            <tbody>
              {data.threads.map((item, index) => {
                return (
                  <tr
                    key={index}
                    className={`border-b ${
                      item.thread_status === 1 && "bg-gray-300"
                    }`}
                  >
                    <td>
                      <div className="py-2 flex flex-col items-center">
                        {item.thread_send_receive_status === 1 && (
                          <Chip text="送信済" width="5rem" color="#4a88cb" />
                        )}
                        {item.thread_send_receive_status === 2 && (
                          <Chip text="受信" width="5rem" color="#fb545a" />
                        )}
                        {item.thread_type === 0 && item.mit_req_url ? (
                          <a
                            href={item.mit_req_url ?? ""}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline text-jhk-blue"
                          >
                            {item.thread_type_name}
                          </a>
                        ) : (
                          <div>{item.thread_type_name}</div>
                        )}
                      </div>
                    </td>
                    <td>{item.corp_name}</td>
                    <td>
                      <Link
                        to={`/talk-detail/${item.thread_id}/`}
                        className="hover:underline text-jhk-blue"
                      >
                        {item.thread_title}
                      </Link>
                    </td>
                    <td>
                      <div>
                        {item.last_update_type === 0 && <>自分</>}
                        {item.last_update_type === 1 && <>相手</>}
                      </div>
                      <div>{Utils.Date.format(item.last_update_datetime)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex justify-center m-4">
            <Pagination
              page={options.current?.page_no ?? 1}
              data={data}
              onChange={onChangePage}
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default TalkListPage;
